import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, API_URL } from '../../services/apiService';
import { Article, CATEGORY_LABELS } from '../../types';
import { resolveImageUrl } from '../../utils/images';

const WS_URL = API_URL.replace(/^http/, 'ws');

// ─── Agent → phase/step metadata ──────────────────────────────────────────────
const AGENT_META: Record<string, { step: number; phase: string; label: string; desc: string }> = {
  orchestrator:        { step: 0,  phase: 'Discovery',     label: 'Orchestrator',       desc: 'Initialising pipeline...' },
  news_discovery:      { step: 1,  phase: 'Discovery',     label: 'News Discovery',      desc: 'Scanning RSS feeds, LME and Reuters for aluminium news' },
  source_eligibility:  { step: 2,  phase: 'Discovery',     label: 'Source Eligibility',  desc: 'Filtering sources for quality and relevance' },
  source_extraction:   { step: 3,  phase: 'Discovery',     label: 'Source Extraction',   desc: 'Extracting facts, quotes and key entities' },
  source_comparison:   { step: 4,  phase: 'Discovery',     label: 'Source Comparison',   desc: 'Cross-checking facts across multiple sources' },
  fact_verification:   { step: 5,  phase: 'Verification',  label: 'Fact Verification',   desc: 'Validating every number, date and claim' },
  claim_verification:  { step: 6,  phase: 'Verification',  label: 'Claim Verification',  desc: 'Running targeted checks on all assertions' },
  research:            { step: 7,  phase: 'Verification',  label: 'Deep Research',       desc: 'Sourcing additional evidence for contested facts' },
  fact_bank:           { step: 8,  phase: 'Verification',  label: 'Fact Bank',           desc: 'Locking approved facts — no fabrication beyond this point' },
  article_generation:  { step: 9,  phase: 'Writing',       label: 'Article Generation',  desc: 'Writing article from verified facts only' },
  fact_mapping:        { step: 10, phase: 'Writing',       label: 'Fact Mapping',        desc: 'Mapping every claim back to its source fact' },
  fidelity_check:      { step: 11, phase: 'Quality',       label: 'Fidelity Gate',       desc: 'Checking accuracy, coverage and unsupported claims' },
  style_matcher:       { step: 12, phase: 'Quality',       label: 'Style Validation',    desc: 'Verifying tone and language match AlCircle style' },
  style_validation:    { step: 12, phase: 'Quality',       label: 'Style Validation',    desc: 'Verifying tone and language match AlCircle style' },
  plagiarism_check:    { step: 13, phase: 'Quality',       label: 'Plagiarism Check',    desc: 'Ensuring the article is original content' },
  rewrite_check:       { step: 14, phase: 'Quality',       label: 'Rewrite Gate',        desc: 'Deciding whether a revision is needed' },
  rewrite:             { step: 14, phase: 'Quality',       label: 'Rewriting',           desc: 'Revising article to meet quality thresholds' },
  fact_integrity:      { step: 15, phase: 'Quality',       label: 'Fact Integrity',      desc: 'Final check: no post-generation facts introduced' },
  image_generation:    { step: 16, phase: 'Publish',       label: 'Image Generation',    desc: 'Generating editorial hero image with Imagen4' },
  final_qa:            { step: 17, phase: 'Publish',       label: 'Final QA',            desc: 'Last editorial review before marking complete' },
};

const TOTAL_STEPS = 17;

const PHASES = ['Discovery', 'Verification', 'Writing', 'Quality', 'Publish'];

const PHASE_ACCENT: Record<string, string> = {
  Discovery:    '#22d3ee',
  Verification: '#f59e0b',
  Writing:      '#a78bfa',
  Quality:      '#34d399',
  Publish:      '#4f46e5',
};

// ─── Pipeline phases for the collapsible section ───────────────────────────────
const PIPELINE_PHASES = [
  {
    phase: 'Discovery', icon: 'travel_explore', color: 'cyan',
    agents: [
      { id: 'news_discovery',    name: 'News Discovery',    desc: 'Scans RSS feeds, LME and Reuters for aluminium news', icon: 'rss_feed' },
      { id: 'source_extraction', name: 'Source Extraction', desc: 'Pulls out facts, quotes, numbers and key entities',    icon: 'data_object' },
      { id: 'source_comparison', name: 'Source Comparison', desc: 'Cross-checks facts across multiple news sources',      icon: 'compare_arrows' },
    ],
  },
  {
    phase: 'Verification', icon: 'verified_user', color: 'amber',
    agents: [
      { id: 'fact_verification', name: 'Fact Verification', desc: 'Validates all numbers, dates and claims for accuracy',     icon: 'fact_check' },
      { id: 'research',          name: 'Deep Research',     desc: 'Finds additional sources when facts conflict', icon: 'manage_search', conditional: true },
    ],
  },
  {
    phase: 'Writing', icon: 'edit_note', color: 'purple',
    agents: [
      { id: 'article_generation', name: 'Article Generation', desc: 'Writes a complete article from verified facts', icon: 'article' },
    ],
  },
  {
    phase: 'Quality', icon: 'shield', color: 'emerald',
    agents: [
      { id: 'style_validation', name: 'Style Validation', desc: 'Checks tone and language match AlCircle editorial style', icon: 'style' },
      { id: 'plagiarism_check', name: 'Plagiarism Check', desc: 'Ensures content is original and not copied',              icon: 'security' },
      { id: 'rewrite_check',   name: 'Rewrite Gate',     desc: 'Decides if article needs revision',                        icon: 'rule' },
      { id: 'rewrite',         name: 'Rewrite',          desc: 'Revises the article (up to 2 times if needed)',           icon: 'replay', conditional: true },
    ],
  },
  {
    phase: 'Publish', icon: 'rocket_launch', color: 'primary',
    agents: [
      { id: 'image_generation', name: 'Image Generation', desc: 'Creates a hero image for the article',   icon: 'image' },
      { id: 'final_qa',         name: 'Final QA',         desc: 'Last review before marking as published', icon: 'task_alt' },
    ],
  },
];

const PHASE_STYLES: Record<string, { accent: string; faint: string; border: string }> = {
  cyan:    { accent: '#22d3ee', faint: 'rgba(34,211,238,0.07)',  border: 'rgba(34,211,238,0.2)' },
  amber:   { accent: '#f59e0b', faint: 'rgba(245,158,11,0.07)',  border: 'rgba(245,158,11,0.2)' },
  purple:  { accent: '#a78bfa', faint: 'rgba(167,139,250,0.07)', border: 'rgba(167,139,250,0.2)' },
  emerald: { accent: '#34d399', faint: 'rgba(52,211,153,0.07)',  border: 'rgba(52,211,153,0.2)' },
  primary: { accent: '#4f46e5', faint: 'rgba(79,70,229,0.07)',  border: 'rgba(79,70,229,0.2)' },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmtDuration = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s % 60}s`;
};

// ─── Main component ────────────────────────────────────────────────────────────
const PipelineHub: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [traceLog, setTraceLog] = useState<Array<{ time: string; agent: string; text: string }>>([]);
  const terminalRef = useRef<HTMLDivElement>(null);

  // Live run tracking
  const [runStartTime, setRunStartTime] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [currentAgentId, setCurrentAgentId] = useState<string>('');
  const [liveStats, setLiveStats] = useState({ news: 0, articles: 0, images: 0 });
  const [lastRunAt, setLastRunAt] = useState<Date | null>(() => {
    try {
      const s = sessionStorage.getItem('last_pipeline_run');
      return s ? new Date(s) : null;
    } catch { return null; }
  });
  // stepTimings[i] = timestamp when step i was first seen — for ETA
  const stepTimingsRef = useRef<number[]>([]);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── On mount: restore running state if backend pipeline is still active ──────
  useEffect(() => {
    let pollTimer: ReturnType<typeof setInterval>;

    const checkStatus = async () => {
      try {
        const status = await apiService.getGlobalPipelineStatus();
        if (status.active) {
          // Compute how long it's been running
          const startedAt = status.started_at ? new Date(status.started_at).getTime() : Date.now();
          setRunStartTime(startedAt);
          setElapsed(Date.now() - startedAt);
          setIsProcessing(true);

          // Keep polling until pipeline finishes
          pollTimer = setInterval(async () => {
            try {
              const s = await apiService.getGlobalPipelineStatus();
              if (!s.active) {
                setIsProcessing(false);
                setRunStartTime(null);
                clearInterval(pollTimer);
                const now = new Date();
                setLastRunAt(now);
                try { sessionStorage.setItem('last_pipeline_run', now.toISOString()); } catch {}
                // Refresh articles
                const data = await apiService.getArticles();
                if (data?.articles) setArticles(data.articles);
              }
            } catch {}
          }, 5000);
        }
      } catch {}
    };

    checkStatus();
    return () => { clearInterval(pollTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiService.getArticles().then(data => {
      if (data?.articles) setArticles(data.articles);
    }).catch(() => {});
  }, []);

  // Elapsed clock — ticks whenever isProcessing is true
  useEffect(() => {
    if (isProcessing) {
      elapsedTimerRef.current = setInterval(() => {
        setElapsed(prev => runStartTime ? Date.now() - runStartTime : prev + 1000);
      }, 1000);
    } else {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    }
    return () => { if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current); };
  }, [isProcessing, runStartTime]);

  // WebSocket
  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    const connect = () => {
      try {
        ws = new WebSocket(`${WS_URL}/ws/workflow/workflow_0`);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // Update live stats from progress field
            if (data.progress) {
              setLiveStats({
                news:     data.progress.news     || 0,
                articles: data.progress.articles || 0,
                images:   data.progress.images   || 0,
              });
            }

            if (data.current_agent && data.current_agent !== 'orchestrator') {
              const agentId = data.current_agent;
              setCurrentAgentId(agentId);
              addTrace(agentId, AGENT_META[agentId]?.desc || 'Processing...');

              // Record timing for ETA
              const meta = AGENT_META[agentId];
              if (meta && meta.step > 0) {
                const now = Date.now();
                if (!stepTimingsRef.current[meta.step]) {
                  stepTimingsRef.current[meta.step] = now;
                }
              }
            }

            if (data.articles?.length > 0) setArticles(data.articles);
          } catch {}
        };
        ws.onerror = () => {};
        ws.onclose = () => { reconnectTimer = setTimeout(connect, 5000); };
      } catch {}
    };
    connect();
    return () => { clearTimeout(reconnectTimer); ws?.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addTrace = useCallback((agent: string, text: string) => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    setTraceLog(prev => [...prev.slice(-50), { time, agent, text }]);
    setTimeout(() => terminalRef.current?.scrollTo({ top: terminalRef.current.scrollHeight, behavior: 'smooth' }), 50);
  }, []);

  const handleTriggerRun = async () => {
    const startTs = Date.now();
    setRunStartTime(startTs);
    setElapsed(0);
    setCurrentAgentId('');
    setLiveStats({ news: 0, articles: 0, images: 0 });
    stepTimingsRef.current = [];
    setIsProcessing(true);
    setShowPipeline(false);
    addTrace('System', 'Pipeline started — discovering news...');
    try {
      const response = await apiService.runWorkflow();
      if (response?.articles) {
        const all = await apiService.getArticles();
        setArticles(all?.articles || response.articles);
        addTrace('System', `Done — ${response.articles.length} new articles generated.`);
      }
    } catch (err) {
      addTrace('System', `Error: ${(err as Error).message}`);
    } finally {
      setIsProcessing(false);
      setRunStartTime(null);
      const now = new Date();
      setLastRunAt(now);
      try { sessionStorage.setItem('last_pipeline_run', now.toISOString()); } catch {}
    }
  };

  // ETA calculation
  const computeEta = (): string | null => {
    const meta = AGENT_META[currentAgentId];
    const currentStep = meta?.step || 0;
    const timings = stepTimingsRef.current.filter(Boolean);
    if (!runStartTime || timings.length < 2 || currentStep === 0) return null;
    const avgMs = elapsed / currentStep;
    const remaining = TOTAL_STEPS - currentStep;
    if (remaining <= 0) return null;
    const etaMs = avgMs * remaining;
    return fmtDuration(etaMs);
  };

  const totalArticles = articles.length;
  const publishedCount = articles.filter(a => a.status === 'published').length;
  const draftCount = articles.filter(a => a.status === 'draft' || a.status === 'review').length;
  const avgStyle = totalArticles > 0 ? Math.round(articles.reduce((s, a) => s + (a.style_match_score || 0), 0) / totalArticles) : 0;
  const avgOriginality = totalArticles > 0 ? Math.round(100 - articles.reduce((s, a) => s + (a.plagiarism_score || 0), 0) / totalArticles) : 0;
  const recentArticles = [...articles].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()).slice(0, 5);

  const formatTimeAgo = (dateStr?: string) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const currentMeta = AGENT_META[currentAgentId];
  const currentStep = currentMeta?.step || 0;
  const currentPhase = currentMeta?.phase || 'Discovery';
  const eta = computeEta();
  const progressPct = TOTAL_STEPS > 0 ? Math.round((currentStep / TOTAL_STEPS) * 100) : 0;

  return (
    <div className="flex flex-col w-full gap-6">

      {/* ── Hero / Live Run Monitor ── */}
      {isProcessing ? (
        /* ── LIVE RUN MONITOR ─────────────────────────────────────── */
        <div className="relative overflow-hidden rounded-2xl" style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
        }}>
          {/* Shimmer accent line */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg, #3730a3, ${PHASE_ACCENT[currentPhase] || '#4f46e5'}, #6366f1)`,
            backgroundSize: '200% 100%',
            animation: 'lrm-shimmer 2s ease-in-out infinite',
          }} />
          <style>{`
            @keyframes lrm-shimmer { 0%,100%{background-position:0%} 50%{background-position:100%} }
            @keyframes lrm-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
            @keyframes lrm-step-appear { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
          `}</style>

          <div className="p-6">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full font-mono text-label-xs" style={{ background: 'rgba(79,70,229,0.15)', border: '1px solid rgba(79,70,229,0.3)', color: '#4f46e5' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#4f46e5', animation: 'lrm-pulse 1.4s ease-in-out infinite' }} />
                  PIPELINE RUNNING
                </div>
                <span className="font-mono text-label-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  Step {currentStep} / {TOTAL_STEPS}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-mono text-label-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Elapsed</div>
                  <div className="font-mono text-label-sm font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>{fmtDuration(elapsed)}</div>
                </div>
                {eta && (
                  <div className="text-right">
                    <div className="font-mono text-label-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Est. remaining</div>
                    <div className="font-mono text-label-sm font-bold" style={{ color: '#4f46e5' }}>{eta}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Phase chain */}
            <div className="flex items-center gap-1 mb-5 overflow-x-auto pb-1">
              {PHASES.map((phase, i) => {
                const phaseIdx = PHASES.indexOf(currentPhase);
                const isDone = i < phaseIdx;
                const isActive = phase === currentPhase;
                const accent = PHASE_ACCENT[phase];
                return (
                  <React.Fragment key={phase}>
                    <div
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-shrink-0 transition-all"
                      style={{
                        background: isActive ? `${accent}20` : isDone ? 'rgba(255,255,255,0.05)' : 'transparent',
                        border: isActive ? `1px solid ${accent}50` : '1px solid transparent',
                      }}
                    >
                      {isDone && (
                        <span className="material-symbols-outlined text-[13px]" style={{ color: accent }}>check_circle</span>
                      )}
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: accent, animation: 'lrm-pulse 1.4s ease-in-out infinite' }} />
                      )}
                      <span
                        className="font-mono text-label-xs whitespace-nowrap"
                        style={{ color: isActive ? accent : isDone ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.18)' }}
                      >
                        {phase}
                      </span>
                    </div>
                    {i < PHASES.length - 1 && (
                      <span className="material-symbols-outlined text-[12px] flex-shrink-0" style={{ color: i < phaseIdx ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)' }}>
                        chevron_right
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Current step headline */}
            <div key={currentAgentId} style={{ animation: 'lrm-step-appear 0.3s ease' }}>
              <div className="font-mono text-label-xs mb-1" style={{ color: PHASE_ACCENT[currentPhase] || '#4f46e5' }}>
                {currentPhase.toUpperCase()} · {currentMeta?.label || 'Initialising'}
              </div>
              <h2 className="font-display text-headline-md mb-1" style={{ color: '#ffffff', letterSpacing: '-0.02em' }}>
                {currentMeta?.desc || 'Starting pipeline...'}
              </h2>
            </div>

            {/* Progress bar */}
            <div className="mt-4 mb-4">
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${progressPct}%`,
                    background: `linear-gradient(90deg, ${PHASE_ACCENT[currentPhase] || '#4f46e5'}, rgba(255,255,255,0.6))`,
                  }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="font-mono text-label-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>0</span>
                <span className="font-mono text-label-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>{TOTAL_STEPS} steps</span>
              </div>
            </div>

            {/* Live stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Stories found', value: liveStats.news,     icon: 'newspaper' },
                { label: 'Articles written', value: liveStats.articles, icon: 'article' },
                { label: 'Images generated', value: liveStats.images,   icon: 'image' },
              ].map(({ label, value, icon }) => (
                <div key={label} className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <span className="material-symbols-outlined text-[18px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{icon}</span>
                  <div>
                    <div className="font-display text-headline-sm" style={{ color: '#ffffff' }}>{value}</div>
                    <div className="font-mono text-label-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Last 3 trace lines inline */}
            {traceLog.length > 0 && (
              <div className="mt-4 rounded-xl p-3" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                {traceLog.slice(-3).map((entry, i) => (
                  <div key={i} className="flex items-start gap-2 font-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                    <span style={{ color: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>{entry.time}</span>
                    <span style={{ color: '#4f46e5', flexShrink: 0 }}>{entry.agent}</span>
                    <span className="truncate">{entry.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── NORMAL HERO ────────────────────────────────────────────── */
        <div className="relative overflow-hidden rounded-2xl p-8" style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
        }}>
          <div style={{ position: 'absolute', top: -60, right: -60, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -40, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <p className="font-mono text-label-xs uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
                AlCircle · Editorial Intelligence
              </p>
              <h1 className="font-display text-headline-lg" style={{ color: '#ffffff', letterSpacing: '-0.02em' }}>
                {totalArticles > 0 ? `${totalArticles} articles in your catalog` : 'Ready to generate'}
              </h1>
              <p className="font-body text-body-lg mt-2 max-w-xl" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {totalArticles > 0
                  ? draftCount > 0
                    ? `${draftCount} article${draftCount > 1 ? 's' : ''} waiting for review.`
                    : 'All articles reviewed.'
                  : 'Discover aluminium industry news and generate publication-ready articles in one run.'
                }
              </p>
              {lastRunAt && (
                <p className="font-mono text-label-xs mt-3 flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  <span className="material-symbols-outlined text-[13px]">schedule</span>
                  Last run: {lastRunAt.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            <button
              onClick={handleTriggerRun}
              className="flex items-center gap-3 px-8 py-4 rounded-xl font-display text-headline-sm transition-all flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                color: '#ffffff',
                boxShadow: '0 6px 24px rgba(79,70,229,0.4)',
              }}
            >
              <span className="material-symbols-outlined text-[22px]">bolt</span>
              <div>Generate New Articles</div>
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { icon: 'article',       label: 'Total Articles',  value: totalArticles,        accent: '#4f46e5', onClick: () => navigate('/content') },
          { icon: 'edit_note',     label: 'Pending Review',  value: draftCount,           accent: '#f59e0b', onClick: () => navigate('/content'), alert: draftCount > 0 },
          { icon: 'check_circle',  label: 'Published',       value: publishedCount,       accent: '#34d399', onClick: () => navigate('/content') },
          { icon: 'style',         label: 'Avg Style Score', value: `${avgStyle}%`,       accent: '#4f46e5' },
          { icon: 'verified_user', label: 'Avg Originality', value: `${avgOriginality}%`, accent: '#34d399' },
        ].map(({ icon, label, value, accent, onClick, alert }) => (
          <div
            key={label}
            onClick={onClick}
            className="rounded-xl p-4 flex items-center gap-3 transition-all"
            style={{
              background: '#ffffff',
              border: alert ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              cursor: onClick ? 'pointer' : 'default',
            }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}18` }}>
              <span className="material-symbols-outlined text-[20px]" style={{ color: accent }}>{icon}</span>
            </div>
            <div>
              <div className="font-display text-headline-md" style={{ color: accent }}>{value}</div>
              <div className="font-body text-body-sm" style={{ color: '#475569' }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Articles */}
      {recentArticles.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-headline-sm" style={{ color: '#0f172a' }}>Recent Articles</h2>
            <button onClick={() => navigate('/content')} className="font-body text-body-sm flex items-center gap-1" style={{ color: '#4f46e5' }}>
              View all <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {recentArticles.slice(0, 3).map(article => {
              const imageUrl = resolveImageUrl(article.generated_image_url || article.imageUrl);
              const hasImage = !!imageUrl;
              return (
                <div
                  key={article.id}
                  className="group rounded-xl overflow-hidden cursor-pointer transition-all duration-200"
                  style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
                  onClick={() => navigate(`/article/${article.id}`)}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.12)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                  {hasImage ? (
                    <div className="h-44 overflow-hidden" style={{ background: '#0f172a' }}>
                      <img src={imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" loading="lazy" />
                    </div>
                  ) : (
                    <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #4f46e5, #6366f1)' }} />
                  )}
                  <div className="p-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <ArticleStatusDot status={article.status} />
                      <span className="font-body text-[11px] capitalize" style={{ color: '#64748b' }}>{article.status}</span>
                      {article.category && (
                        <span className="px-1.5 py-0.5 rounded font-body text-[10px]" style={{ background: '#f1f5f9', color: '#475569' }}>
                          {CATEGORY_LABELS[article.category] || article.category}
                        </span>
                      )}
                      <span className="font-body text-[11px] ml-auto" style={{ color: '#94a3b8' }}>{formatTimeAgo(article.created_at)}</span>
                    </div>
                    <h3 className="font-display text-headline-sm leading-snug line-clamp-2" style={{ color: '#0f172a' }}>
                      {article.headline}
                    </h3>
                    <div className="flex items-center gap-3 mt-auto pt-2" style={{ borderTop: '1px solid #f1f5f9' }}>
                      <MiniScore label="Style" value={article.style_match_score || 0} />
                      <MiniScore label="Original" value={100 - (article.plagiarism_score || 0)} />
                      <span className="font-body text-[11px] ml-auto" style={{ color: '#94a3b8' }}>{article.news_source}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {recentArticles.length > 3 && (
            <div className="mt-3 rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}>
              {recentArticles.slice(3).map((article, i, arr) => (
                <div
                  key={article.id}
                  className="flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors"
                  style={{ borderBottom: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none' }}
                  onClick={() => navigate(`/article/${article.id}`)}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <ArticleStatusDot status={article.status} />
                  <h4 className="font-body text-body-md flex-1 line-clamp-1" style={{ color: '#0f172a' }}>{article.headline}</h4>
                  <span className="font-body text-body-sm hidden sm:block" style={{ color: '#64748b' }}>{article.news_source}</span>
                  <span className="font-body text-[11px] flex-shrink-0" style={{ color: '#94a3b8' }}>{formatTimeAgo(article.created_at)}</span>
                  <span className="material-symbols-outlined text-[16px]" style={{ color: '#cbd5e1' }}>chevron_right</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Pipeline — collapsible, hidden during run */}
      {!isProcessing && (
        <section>
          <button
            onClick={() => setShowPipeline(!showPipeline)}
            className="w-full flex items-center justify-between p-4 rounded-xl transition-all"
            style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(79,70,229,0.1)' }}>
                <span className="material-symbols-outlined text-[22px]" style={{ color: '#4f46e5' }}>account_tree</span>
              </div>
              <div className="text-left">
                <h2 className="font-display text-headline-sm" style={{ color: '#0f172a' }}>How Articles Are Generated</h2>
              </div>
            </div>
            <span className={`material-symbols-outlined text-[20px] transition-transform ${showPipeline ? 'rotate-180' : ''}`} style={{ color: '#94a3b8' }}>expand_more</span>
          </button>
          {showPipeline && (
            <div className="mt-3 rounded-xl p-6" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}>
              <div className="flex flex-col gap-4">
                {PIPELINE_PHASES.map((phase, phaseIdx) => {
                  const s = PHASE_STYLES[phase.color] || PHASE_STYLES.primary;
                  return (
                    <div key={phase.phase}>
                      {phaseIdx > 0 && (
                        <div className="flex justify-center py-1">
                          <span className="material-symbols-outlined text-[14px]" style={{ color: '#cbd5e1' }}>south</span>
                        </div>
                      )}
                      <div className="rounded-xl p-4" style={{ background: s.faint, border: `1px solid ${s.border}` }}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.6)' }}>
                            <span className="material-symbols-outlined text-[18px]" style={{ color: s.accent }}>{phase.icon}</span>
                          </div>
                          <span className="font-display text-headline-sm" style={{ color: s.accent }}>{phase.phase}</span>
                          <span className="font-body text-body-sm" style={{ color: '#94a3b8' }}>{phase.agents.length} agent{phase.agents.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className={`grid gap-3 ${phase.agents.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                          {phase.agents.map(agent => {
                            const cond = (agent as any).conditional;
                            return (
                              <div key={agent.id} className="rounded-lg p-3 flex items-start gap-3" style={{ background: 'rgba(255,255,255,0.7)', border: cond ? '1px dashed rgba(0,0,0,0.12)' : '1px solid rgba(0,0,0,0.07)' }}>
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f1f5f9' }}>
                                  <span className="material-symbols-outlined text-[18px]" style={{ color: '#94a3b8' }}>{agent.icon}</span>
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-display text-body-md font-semibold" style={{ color: '#0f172a' }}>{agent.name}</span>
                                    {cond && <span className="font-body text-[9px] px-1 py-0.5 rounded" style={{ background: '#f1f5f9', color: '#94a3b8' }}>IF NEEDED</span>}
                                  </div>
                                  <p className="font-body text-body-sm mt-0.5" style={{ color: '#64748b' }}>{agent.desc}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Activity Log */}
      {traceLog.length > 0 && !isProcessing && (
        <section>
          <button
            onClick={() => setShowLog(!showLog)}
            className="w-full flex items-center justify-between p-4 rounded-xl"
            style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
          >
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-[20px]" style={{ color: '#4f46e5' }}>terminal</span>
              <span className="font-display text-headline-sm" style={{ color: '#0f172a' }}>Activity Log</span>
              <span className="font-body text-body-sm ml-1" style={{ color: '#94a3b8' }}>{traceLog.length} entries</span>
            </div>
            <span className={`material-symbols-outlined text-[20px] transition-transform ${showLog ? 'rotate-180' : ''}`} style={{ color: '#94a3b8' }}>expand_more</span>
          </button>
          {showLog && (
            <div className="mt-3 rounded-xl overflow-hidden" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div ref={terminalRef} className="max-h-[280px] p-4 font-mono text-label-sm overflow-y-auto flex flex-col gap-1.5">
                {traceLog.map((entry, i) => (
                  <div key={i} className="flex items-start gap-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    <span className="shrink-0 text-[11px]" style={{ color: 'rgba(255,255,255,0.25)' }}>{entry.time}</span>
                    <span className="font-semibold shrink-0 text-[11px]" style={{ color: '#4f46e5' }}>{entry.agent}</span>
                    <span className="text-[11px]">{entry.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Empty state */}
      {totalArticles === 0 && !isProcessing && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'rgba(79,70,229,0.1)', border: '1px solid rgba(79,70,229,0.2)' }}>
            <span className="material-symbols-outlined text-[36px]" style={{ color: '#4f46e5' }}>auto_awesome</span>
          </div>
          <h3 className="font-display text-headline-md mb-2" style={{ color: '#0f172a' }}>No articles yet</h3>
          <p className="font-body text-body-md max-w-md mb-6" style={{ color: '#64748b' }}>
            Click "Generate New Articles" to discover aluminium industry news and automatically create publication-ready articles.
          </p>
          <div className="flex items-center gap-6" style={{ color: '#64748b' }}>
            {[['travel_explore', 'Discovers news from 50+ sources'], ['fact_check', 'Verifies every claim'], ['image', 'Generates hero images']].map(([icon, text]) => (
              <div key={icon} className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[16px]" style={{ color: '#4f46e5' }}>{icon}</span>
                <span className="font-body text-body-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ArticleStatusDot: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = { draft: '#f59e0b', published: '#34d399', revised: '#4f46e5', review: '#64748b' };
  return <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors[status] || '#94a3b8' }} />;
};

const MiniScore: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const color = value >= 85 ? '#34d399' : value >= 70 ? '#4f46e5' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-1">
      <span className="font-body text-[10px]" style={{ color: '#94a3b8' }}>{label}</span>
      <span className="font-mono text-[11px] font-bold" style={{ color }}>{Math.round(value)}%</span>
    </div>
  );
};

export default PipelineHub;
